'use client';

import { c as _c } from "react/compiler-runtime";
import * as Sentry from '@sentry/nextjs';
import { useEffect, useRef, useState } from 'react';
import { ADPOPCORN_SSP_MODULE, MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { useAdpopcornSspEvents } from './useAdpopcornSspEvents';
export type AdpopcornSspFullscreenFormat = typeof ADPOPCORN_SSP_MODULE.INTERSTITIAL_VIDEO | typeof ADPOPCORN_SSP_MODULE.REWARD_VIDEO;
export type AdpopcornSspFullscreenStatus = 'idle' | 'loading' | 'loaded' | 'load-failed' | 'showing' | 'show-failed' | 'closed' | 'completed' | 'reward-completed';

//* 광고 트리거의 UI 파생 상태 — 소비처가 버튼(로딩/비활성/안내)을 통일되게 렌더하는 단일 진실.
export type AdTriggerPhase = 'ready' | 'pending' | 'cooldown';

//* no-fill 실패 후 재시도까지 잠그는 쿨다운. 연타만 끊고 재시도는 계속 허용(락아웃 금지).
const RETRY_COOLDOWN_MS = 3_000;
const KEYWORD_MAP = {
  [ADPOPCORN_SSP_MODULE.INTERSTITIAL_VIDEO]: {
    load: MESSAGE_KEYWORD.LOAD_ADPOPCORN_SSP_INTERSTITIAL_VIDEO,
    show: MESSAGE_KEYWORD.SHOW_ADPOPCORN_SSP_INTERSTITIAL_VIDEO
  },
  [ADPOPCORN_SSP_MODULE.REWARD_VIDEO]: {
    load: MESSAGE_KEYWORD.LOAD_ADPOPCORN_SSP_REWARD_VIDEO,
    show: MESSAGE_KEYWORD.SHOW_ADPOPCORN_SSP_REWARD_VIDEO
  }
} as const;

// 네이티브 SDK가 emit하는 이벤트 이름 → 상태 매핑 (앱이 이름 그대로 포워딩).
// OpenFalied 오탈자는 SDK 원본 그대로다.
const EVENT_STATUS_MAP: Record<AdpopcornSspFullscreenFormat, Record<string, AdpopcornSspFullscreenStatus>> = {
  [ADPOPCORN_SSP_MODULE.INTERSTITIAL_VIDEO]: {
    OnInterstitialVideoAdLoaded: 'loaded',
    OnInterstitialVideoAdLoadFailed: 'load-failed',
    OnInterstitialVideoAdOpened: 'showing',
    OnInterstitialVideoAdOpenFalied: 'show-failed',
    OnInterstitialVideoAdClosed: 'closed'
  },
  [ADPOPCORN_SSP_MODULE.REWARD_VIDEO]: {
    OnRewardVideoAdLoaded: 'loaded',
    OnRewardVideoAdLoadFailed: 'load-failed',
    OnRewardVideoAdOpened: 'showing',
    OnRewardVideoAdOpenFalied: 'show-failed',
    OnRewardVideoAdClosed: 'closed',
    OnRewardVideoPlayCompleted: 'completed',
    OnRewardPlusCompleted: 'reward-completed'
  }
};
interface UseAdpopcornSspFullscreenAdOptions {
  /** 'loaded' 이벤트 수신 시 마지막 load의 placementId로 자동 show한다(load→show 수동 순서를 감춘다). */
  autoShow?: boolean;
  /** 리워드영상 재생 완료(OnRewardVideoPlayCompleted) 시 호출 — payload에 리워드 금액은 없다. */
  onCompleted?: (payload: unknown) => void;
  /**
   * 리워드 적립 완료(OnRewardPlusCompleted) 시 호출 — payload에 resultCode·reward(적립 포인트)가 담긴다.
   * 애드팝콘 SSP는 S2S 콜백을 제공하지 않으므로 이 클라 이벤트가 지급 트리거다.
   * reward 금액을 서버 지급 API로 넘기며, 서버가 검증·멱등 처리를 책임진다.
   */
  onRewardPlus?: (payload: unknown) => void;
  /** 광고 닫힘(closed) 시 호출 — 완료 이벤트가 없는 전면영상의 지급 트리거. */
  onClosed?: (payload: unknown) => void;
  /** 광고 노출 시작(showing) 시 호출 — 전면이 화면을 덮은 동안 아래 화면을 미리 전환하는 용도. */
  onOpened?: (payload: unknown) => void;
}

/**
 * AdPopcorn SSP 전면 계열(전면영상/리워드영상) 광고 훅.
 * load(placementId) → 'loaded' 이벤트 수신 → show(placementId) 순서로 사용한다.
 * autoShow를 켜면 'loaded'에서 마지막 load의 placementId로 자동 show한다.
 */
export const useAdpopcornSspFullscreenAd = (format, t0) => {
  const $ = _c(29);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    autoShow,
    onCompleted,
    onRewardPlus,
    onClosed,
    onOpened
  } = t1;
  const [status, setStatus] = useState("idle");
  const [isCoolingDown, setIsCoolingDown] = useState(false);
  const placementIdRef = useRef("");
  const isOwnCycleRef = useRef(false);
  const cooldownTimerRef = useRef(null);
  const t2 = KEYWORD_MAP[format];
  let t3;
  if ($[2] !== t2.load) {
    t3 = {
      type: t2.load
    };
    $[2] = t2.load;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    request: requestLoad,
    response: loadResponse
  } = useHandleApp(t3);
  const t4 = KEYWORD_MAP[format];
  let t5;
  if ($[4] !== t4.show) {
    t5 = {
      type: t4.show
    };
    $[4] = t4.show;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const {
    request: requestShow,
    response: showResponse
  } = useHandleApp(t5);
  let t6;
  if ($[6] !== requestLoad || $[7] !== status) {
    t6 = placementId => {
      if (status === "loading" || status === "loaded" || status === "showing") {
        return;
      }
      placementIdRef.current = placementId;
      isOwnCycleRef.current = true;
      setStatus("loading");
      requestLoad({
        placementId
      });
    };
    $[6] = requestLoad;
    $[7] = status;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const load = t6;
  let t7;
  if ($[9] !== requestShow) {
    t7 = placementId_0 => {
      requestShow({
        placementId: placementId_0
      });
    };
    $[9] = requestShow;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const show = t7;
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setIsCoolingDown(true);
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
      }
      cooldownTimerRef.current = setTimeout(() => {
        setIsCoolingDown(false);
      }, RETRY_COOLDOWN_MS);
    };
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const startCooldown = t8;
  let t9;
  if ($[12] !== autoShow || $[13] !== format || $[14] !== onClosed || $[15] !== onCompleted || $[16] !== onOpened || $[17] !== onRewardPlus || $[18] !== show) {
    t9 = data => {
      if (data.module !== format) {
        return;
      }
      if (!isOwnCycleRef.current) {
        return;
      }
      const nextStatus = EVENT_STATUS_MAP[format][data.event];
      if (!nextStatus) {
        return;
      }
      setStatus(nextStatus);
      if (nextStatus === "loaded" && autoShow) {
        show(placementIdRef.current);
      }
      if (nextStatus === "showing") {
        onOpened?.(data.payload);
      }
      if (nextStatus === "completed") {
        onCompleted?.(data.payload);
      }
      if (nextStatus === "reward-completed") {
        onRewardPlus?.(data.payload);
      }
      if (nextStatus === "closed") {
        onClosed?.(data.payload);
      }
      if (nextStatus === "load-failed" || nextStatus === "show-failed") {
        Sentry.captureMessage(`AdPopcorn SSP ${nextStatus}`, {
          level: "warning",
          tags: {
            adpopcornSspFormat: format,
            adpopcornSspStatus: nextStatus
          },
          extra: {
            placementId: placementIdRef.current,
            event: data.event,
            payload: data.payload
          }
        });
        startCooldown();
      }
      if (nextStatus === "closed" || nextStatus === "load-failed" || nextStatus === "show-failed") {
        isOwnCycleRef.current = false;
      }
    };
    $[12] = autoShow;
    $[13] = format;
    $[14] = onClosed;
    $[15] = onCompleted;
    $[16] = onOpened;
    $[17] = onRewardPlus;
    $[18] = show;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  useAdpopcornSspEvents(t9);
  let t10;
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => () => {
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
      }
    };
    t11 = [];
    $[20] = t10;
    $[21] = t11;
  } else {
    t10 = $[20];
    t11 = $[21];
  }
  useEffect(t10, t11);
  let t12;
  bb0: {
    if (status === "loading" || status === "loaded" || status === "showing") {
      t12 = "pending";
      break bb0;
    }
    if (isCoolingDown) {
      t12 = "cooldown";
      break bb0;
    }
    t12 = "ready";
  }
  const phase = t12;
  let t13;
  if ($[22] !== load || $[23] !== loadResponse || $[24] !== phase || $[25] !== show || $[26] !== showResponse || $[27] !== status) {
    t13 = {
      status,
      phase,
      load,
      show,
      loadResponse,
      showResponse
    };
    $[22] = load;
    $[23] = loadResponse;
    $[24] = phase;
    $[25] = show;
    $[26] = showResponse;
    $[27] = status;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  return t13;
};